import {
  useContext, useEffect, useState
} from "react";
import clsx from "clsx";
import { UserAttributes } from "lens-platform-sdk";
import { Input } from "@k8slens/lds";
import { FormField } from "@k8slens/lds-form";

import { ProfileContext } from "src/providers/profile-provider";
import { useLensPlatformClient } from "src/hooks/useLensPlatformClient";
import { useLogout } from "src/hooks/useLogout";
import { useAnalytics } from "src/hooks/useAnalytics";

import Layout from "src/components/Layout/Layout";
import Button from "src/components/Button/TrackedButton";
import Notification from "src/components/Notification/Notification";

import { useUploadAvatar } from "src/hooks/useUploadAvatar";
import styles from "./Profile.module.css";
import { ProfileAvatar } from "./ProfileAvatar";

export const Profile = () => {
  const { track, trackError } = useAnalytics();
  const { profile, updateProfile } = useContext(ProfileContext);
  const logout = useLogout();
  const {
    uploadAvatar, loading: uploadingAvatar, error: uploadAvatarError, response: avatarUploadResult
  } = useUploadAvatar();
  const lensPlatformClient = useLensPlatformClient();
  const [profileError, setProfileError] = useState<null | string>(null);
  const [profileSuccessMessage, setProfileSuccessMessage] = useState<null | string>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const flashDuration = 1500;
  const [firstName, setFirstName] = useState<undefined | string>(undefined);
  const [lastName, setLastName] = useState<undefined | string>(undefined);

  // user attributes returned from get endpoint have a different interface comparing to update endpoint.
  // TODO: Update user attributes to the same format for both endpoints.
  const [userAttributes, setUserAttributes] = useState<UserAttributes>({
    company: ""
  });

  useEffect(() => {
    if (profile?.userAttributes) {
      const company = profile?.userAttributes.find((attribute) => attribute.name === "company");

      setUserAttributes({
        company: company ? company.value : ""
      });
    }

    if (profile?.firstName) {
      setFirstName(profile?.firstName);
    }

    if (profile?.lastName) {
      setLastName(profile?.lastName);
    }
  }, [profile]);

  const handleUpdate = (name: string, value: string) => {
    setUserAttributes((userAttributes) => ({ ...userAttributes, ...{ [name]: value } }));
  };

  const handleUploadAvatar = async (file: File) => {
    if (profile && profile.username && userAttributes && file) {
      const uploaded = await uploadAvatar(file);

      if (uploaded) {
        await updateProfile(profile.username);
      }

      return uploaded;
    }

    return undefined;
  };

  const handleAvatarError = (error: Error) => {
    setProfileError(error?.message);
  };

  const saveProfile = async () => {
    if (profile && profile.username && userAttributes) {
      const updatedProfile = { ...profile };

      // API doesn't allow these fields.
      delete updatedProfile.userAttributes;
      delete updatedProfile.fullname;

      try {
        setIsLoading(true);

        await lensPlatformClient.user
          .updateOne(profile.username, {
            ...updatedProfile, attributes: userAttributes, firstName: firstName || "", lastName: lastName || ""
          });

        setProfileSuccessMessage("Profile updated");

        await updateProfile(profile.username);

        track("Profile updated");
        setIsLoading(false);

        // remove success test
        setTimeout(() => {
          setProfileSuccessMessage(null);
        }, flashDuration);
      } catch (error: unknown) {
        setIsLoading(false);

        trackError("Profile update failed");

        if (error instanceof Error) {
          setProfileError(error.message);
        } else {
          setProfileError("Something went wrong");
        }
      }
    }
  };

  return (
    <Layout className="bg-secondary" profile={profile} logout={logout}>
      {
        profile && (
        <div className={styles.profile}>
          {
              (profileError || uploadAvatarError) && <Notification flashDuration={flashDuration} level="error" message={profileError || uploadAvatarError?.message} type="flash" />
          }

          {
              profileSuccessMessage && <Notification className={styles.serverResponseMessage} flashDuration={flashDuration} level="success" message={profileSuccessMessage} type="flash" />
          }

          {
             !uploadAvatarError && avatarUploadResult && <Notification className={styles.serverResponseMessage} flashDuration={flashDuration} level="success" message="User avatar successfully updated!" type="flash" />
          }
          <section className="mb-6">
            <section className="mb-6">
              <h2 className={clsx("lds-h2-caps", styles.welcome)}>Profile</h2>
              <p>This is your public profile. Information in here may be displayed publicly to other users.</p>
            </section>
            <section className="mb-6">
              <div className="grid xl:grid-cols-2 grid-cols-1">
                <div>
                  <FormField
                    label="First name"
                    labelId="firstNameLabel"
                    id="firstName"
                    errorId="firstNameError"
                  >
                    <Input value={firstName} id="firstName" onChange={(e) => setFirstName(e.target.value)} />
                  </FormField>
                  <FormField
                    label="Last name"
                    labelId="lastNameLabel"
                    id="lastName"
                    errorId="lastNameError"
                  >
                    <Input value={lastName} id="lastName" onChange={(e) => setLastName(e.target.value)} />
                  </FormField>
                  <FormField
                    label="Company"
                    labelId="companyLabel"
                    id="company"
                    errorId="companyError"
                  >
                    <Input value={userAttributes?.company} id="company" onChange={(e) => handleUpdate("company", e.target.value)} />
                  </FormField>
                  <p>
                    All of the fields on this page are optional and can be deleted at any time, and by filling them out,
                    you&apos;re giving us consent to share this data wherever your user profile appears.
                    Please see our privacy policy to learn more about how we use this information.
                  </p>
                  <Button loading={isLoading} label="Update Profile" id="update-profile" onClick={saveProfile} primary />
                </div>
                <div className="grid xl:grid-cols-2 grid-cols-1">
                  <div className="m-6 ">
                    <ProfileAvatar
                      onAvatarError={handleAvatarError}
                      user={profile}
                      loading={uploadingAvatar}
                      onAvatarUpload={handleUploadAvatar}
                    />
                  </div>
                </div>
              </div>
            </section>
          </section>
        </div>
        )
      }
    </Layout>
  );
};
